import { buildQuery } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { getTimezone } from '@calendly/timezone-tools'
import { spaceLarge, spaceSmall, spaceTiny, spaceXSmall } from '@calendly/ui/spacing'
import {
  colorBackgroundStandard,
  colorTextAction,
  colorTextSecondary,
  colorTextStandard,
  fontSizeLarge,
  fontSizeMedium,
  fontSizeSmall,
  lineHeightLabelMedium,
} from '@calendly/ui/theme'
import moment from 'moment-timezone'

import { formatDate, formatTime } from 'services/time_format'

/**
 * SA-3836: Inline styles target external email clients; font-family values are intentional for HTML email
 * compatibility, not in-app CUI typography tokens.
 */
export const generateSuggestTimesHtmlEmail = (
  locale,
  bookingUrl,
  timezone,
  groupedSpots,
  timeNotation,
  maxDays = 7
) => {
  const bookingUrlWithParams = (url) => `${url}?${buildQuery({
    source: 'email',
    timezone
  })}`

  const changeTimeZoneBookingUrl = `${bookingUrl}?${buildQuery({
    source: 'email',
    timezone,
    timezone_picker: 'true'
  })}`

  const visibleGroupedSpots = Object.fromEntries(
    Object.entries(groupedSpots).sort().slice(0, maxDays)
  )

  const renderTimezone = () => {
    const timezoneStyles = [
      'font-family: Helvetica, Arial, sans-serif',
      'font-style: normal',
      'font-weight: normal',
      `font-size: ${fontSizeMedium}`,
      `line-height: ${lineHeightLabelMedium}`,
      `color: ${colorTextSecondary}`
    ].join('; ')

    const changeStyles = [
      `margin-left: ${spaceXSmall}`,
      'text-decoration: none',
      `color: ${colorTextAction}`
    ].join('; ')

    return `
      <div>
        <br />
        <b style="color: ${colorTextStandard}"></b>
        <span style="${timezoneStyles}">${I18n.t('timezones.time_zone', { locale })}: ${getTimezone(timezone, { locale }).name}&nbsp;</span>
        <a style="${changeStyles}" href="${changeTimeZoneBookingUrl}">${I18n.t('booking.invitee.change', { locale })}</a>
      </div>
    `
  }

  const renderDate = (date) => {
    const formattedDate = formatDate(
      moment(date),
      '%a, %b %-d',
      locale
    )

    const dateStyles = [
      'font-family: Helvetica, Arial, sans-serif',
      `font-size: ${fontSizeMedium}`,
      `line-height: ${lineHeightLabelMedium}`,
      `padding-bottom: ${spaceSmall}`,
      `color: ${colorTextStandard}`,
      'font-weight: bold'
    ].join('; ')

    return `
      <tr>
        <td style="text-align: left; margin-top: ${spaceLarge};" key="${date}">
          <span style="${dateStyles}">${formattedDate}&nbsp;</span>
        </td>
      </tr>
    `
  }

  const renderSpots = (spots) => {
    const spotsCount = 5
    const spotsArray = spots.slice().sort()

    const limitedSpots = spotsArray.slice(0, spotsCount)
    return limitedSpots.map((spot) => {
      const time = moment(spot).tz(timezone)
      const formattedTime = formatTime(time, timeNotation)
      const spotUrl = moment(spot).utc().format('YYYYMMDD[T]HHmm[Z]')

      const linkStyles = [
        'font-family: Helvetica, Arial, sans-serif',
        'text-decoration: none',
        `color: ${colorTextAction}`,
        `font-size: ${fontSizeSmall}`
      ].join('; ')

      return `<a href="${bookingUrlWithParams(`${bookingUrl}/${spotUrl}`)}" style="${linkStyles}">${formattedTime}</a>`
    }).join(', ')
  }

  const renderEmailDayTable = (date, spots) => {
    const tableStyles = [
      'margin-top: 16px',
      'text-align: left',
      'border-collapse: collapse',
      'border-spacing: 0',
      'width: 362px'
    ].join('; ')

    return `
      <table key="${date}" style="${tableStyles}">
        <tbody>
          ${renderDate(date)}
          <tr><td>${renderSpots(spots)}</td></tr>
        </tbody>
      </table>
    `
  }

  const renderEmailDays = () => Object.keys(visibleGroupedSpots)
    .map((date) => renderEmailDayTable(date, visibleGroupedSpots[date]))
    .join('')


  const tableStyles = [
    'width: 344px',
    'max-width: 344px',
    'font-family: Helvetica, Arial, sans-serif',
    'font-style: normal',
    'font-weight: normal',
    `font-size: ${fontSizeLarge}`,
    `background-color: ${colorBackgroundStandard}`
  ].join('; ')

  const contentStyles = [
    `padding-bottom: ${spaceTiny}`,
    'font-family: Helvetica, Arial, sans-serif',
    `font-size: ${fontSizeMedium}`,
    `color: ${colorTextStandard}`,
    `background-color: ${colorBackgroundStandard}`,
    'line-height: 1.4',
    'width: 344px'
  ].join('; ')

  return `
    <div style="width: 0; height: 0; overflow: none; background-color: ${colorBackgroundStandard};">
      &zwnj;
    </div>
    <table data-calendly-intent="add-times-to-email" style="${tableStyles}">
      <tbody>
        <tr>
          <td>
            <div style="${contentStyles}">
              ${renderEmailDays()}
              ${renderTimezone()}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <br />
  `
}

export const htmlToPlainText = (html) => html.replace(/<[^>]*>/g, '').replace(/&zwnj;/g, '').trim()
